// src/components/AppSwitcher/AppSwitcher.tsx
// Clicking the grid dots icon opens a popup grid of all Cilpron apps
// Matches the Adobe CC app switcher pattern

import React, { useState, useEffect, useRef } from "react";
import { GridDotsRegular } from "@fluentui/react-icons";

interface Product {
  id: string;
  name: string;
  description: string;
  type: string;
  is_active: boolean;
}

const PRODUCT_META: Record<string, { icon: string; color: string; bg: string }> = {
  "Hospitality Software":       { icon: "🏨", color: "#92400E", bg: "#FEF3C7" },
  "CRM Software":               { icon: "🤝", color: "#1E40AF", bg: "#DBEAFE" },
  "Email Services":             { icon: "✉️",  color: "#065F46", bg: "#D1FAE5" },
  "Swift Grid":                 { icon: "⚡",  color: "#5B21B6", bg: "#EDE9FE" },
  "Accounting":                 { icon: "📊",  color: "#0E7490", bg: "#CFFAFE" },
  "Chat Channel":               { icon: "💬",  color: "#9D174D", bg: "#FCE7F3" },
  "Call App":                   { icon: "📞",  color: "#1E3A5F", bg: "#DBEAFE" },
  "Agentic Customer Services":  { icon: "🤖",  color: "#78350F", bg: "#FEF3C7" },
  "API Access (Pay-As-You-Go)": { icon: "🔌",  color: "#1E40AF", bg: "#EFF6FF" },
  "Main SaaS Platform":         { icon: "🏢",  color: "#1E293B", bg: "#F1F5F9" },
  "White Label Add-on":         { icon: "🎨",  color: "#4C1D95", bg: "#F3E8FF" },
};
const DEFAULT_META = { icon: "📦", color: "#292929", bg: "#F5F5F5" };

// Shorten long names for the grid label
function shortName(name: string): string {
  const map: Record<string, string> = {
    "Hospitality Software":       "Hospitality",
    "CRM Software":               "CRM",
    "Email Services":             "Email",
    "Swift Grid":                 "Swift Grid",
    "Accounting":                 "Accounting",
    "Chat Channel":               "Chat",
    "Call App":                   "Call App",
    "Agentic Customer Services":  "AI Agents",
    "API Access (Pay-As-You-Go)": "API Access",
    "Main SaaS Platform":         "Platform",
    "White Label Add-on":         "White Label",
  };
  return map[name] ?? name.split(" ").slice(0, 2).join(" ");
}

interface AppSwitcherProps {
  products: Product[];
  onAppClick: (product: Product) => void;
}

const AppSwitcher: React.FC<AppSwitcherProps> = ({ products, onAppClick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <style>{`
        .cpn-switcher-trigger {
          width: 32px;
          height: 32px;
          border-radius: 5px;
          border: none;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #292929;
          padding: 6px;
          transition: background 0.15s ease, color 0.15s ease;
          position: relative;
        }
        .cpn-switcher-trigger:hover { background: #ebebeb; }
        .cpn-switcher-trigger.open  { background: #e3e3e3; color: #1473e6; }

        .cpn-switcher-popup {
          position: fixed;
          top: 52px;
          right: 8px;
          z-index: 9999;
          width: 320px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
          overflow: hidden;
          animation: switcherIn 0.2s cubic-bezier(0.16,1,0.3,1) forwards;
          transform-origin: top right;
        }

        @keyframes switcherIn {
          from { opacity: 0; transform: scale(0.92) translateY(-8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }

        .cpn-switcher-header {
          padding: 20px 20px 12px;
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
          font-family: 'Segoe UI', sans-serif;
          border-bottom: 1px solid #f0f0f0;
        }

        .cpn-switcher-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          padding: 12px;
          max-height: 440px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #e0e0e0 transparent;
        }

        .cpn-switcher-app {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 14px 8px 12px;
          border-radius: 12px;
          cursor: pointer;
          border: none;
          background: transparent;
          transition: background 0.12s ease;
          font-family: 'Segoe UI', sans-serif;
        }
        .cpn-switcher-app:hover {
          background: #f4f4f4;
        }
        .cpn-switcher-app:active {
          background: #ebebeb;
        }

        .cpn-switcher-app-icon {
          width: 60px;
          height: 60px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: transform 0.12s ease, box-shadow 0.12s ease;
        }
        .cpn-switcher-app:hover .cpn-switcher-app-icon {
          transform: scale(1.06);
          box-shadow: 0 4px 14px rgba(0,0,0,0.14);
        }

        .cpn-switcher-app-name {
          font-size: 11px;
          font-weight: 500;
          color: #2c2c2c;
          text-align: center;
          line-height: 1.3;
          max-width: 80px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cpn-switcher-footer {
          display: grid;
          grid-template-columns: 1fr 1fr;
          border-top: 1px solid #f0f0f0;
          background: #fafafa;
        }

        .cpn-switcher-footer-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 14px 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-family: 'Segoe UI', sans-serif;
          transition: background 0.12s ease;
          font-size: 12px;
          font-weight: 500;
          color: #4b4b4b;
        }
        .cpn-switcher-footer-btn:hover { background: #f0f0f0; }
        .cpn-switcher-footer-btn:first-child { border-right: 1px solid #f0f0f0; }

        .cpn-switcher-footer-icon {
          font-size: 22px;
        }
      `}</style>

      <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
        {/* Trigger button */}
        <button
          className={`cpn-switcher-trigger${open ? " open" : ""}`}
          onClick={() => setOpen(o => !o)}
          aria-label="App switcher"
          aria-expanded={open}
        >
          <GridDotsRegular style={{ fontSize: 18 }} />
        </button>

        {/* Popup */}
        {open && (
          <div className="cpn-switcher-popup">
            <div className="cpn-switcher-header">Web Apps</div>

            <div className="cpn-switcher-grid">
              {products.map((product) => {
                const m = PRODUCT_META[product.name] ?? DEFAULT_META;
                return (
                  <button
                    key={product.id}
                    className="cpn-switcher-app"
                    onClick={() => { onAppClick(product); setOpen(false); }}
                  >
                    <div
                      className="cpn-switcher-app-icon"
                      style={{ background: m.bg }}
                    >
                      {m.icon}
                    </div>
                    <span className="cpn-switcher-app-name">
                      {shortName(product.name)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Footer — matches Adobe's two-button footer */}
            <div className="cpn-switcher-footer">
              <button className="cpn-switcher-footer-btn" onClick={() => setOpen(false)}>
                <span className="cpn-switcher-footer-icon">🏠</span>
                Cilpron.com
              </button>
              <button
                className="cpn-switcher-footer-btn"
                onClick={() => { setOpen(false); }}
              >
                <span className="cpn-switcher-footer-icon">⊞</span>
                All apps
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AppSwitcher;